import { useEffect, useMemo, useRef, useState } from 'react';
import type { InsightsMessage } from '../../../hooks/useInsightsStream';
import { buildSqlResult } from '../SqlResultCard';
import { formatResultTimestampLabel } from '../insightsUtils';
import type { SqlResultEntry } from '../DataDictionaryModal';

interface UseInsightsSqlResultsOptions {
  messages: InsightsMessage[];
  threadId: string | null;
  isDataDictionaryOpen: boolean;
  isThreadLoading: boolean;
  isThreadHistoryLoading: boolean;
}

interface UseInsightsSqlResultsResult {
  threadSqlResults: SqlResultEntry[];
  sqlResultIdsByAiMessageId: Map<string, string[]>;
  sqlResultById: Map<string, SqlResultEntry>;
  hasUnseenDataResults: boolean;
}

export const useInsightsSqlResults = ({
  messages,
  threadId,
  isDataDictionaryOpen,
  isThreadLoading,
  isThreadHistoryLoading,
}: UseInsightsSqlResultsOptions): UseInsightsSqlResultsResult => {
  const [hasUnseenDataResults, setHasUnseenDataResults] = useState<boolean>(false);
  const lastSeenSqlResultIdRef = useRef<string | null>(null);
  const hasInitializedResultSignalRef = useRef<boolean>(false);

  const threadSqlResults: SqlResultEntry[] = useMemo(() => {
    const results: SqlResultEntry[] = [];
    let counter = 0;

    messages.forEach((message, messageIndex) => {
      if (message.type !== 'data') {
        return;
      }

      const csvText = typeof message.content === 'string' ? message.content : '';
      if (!csvText.trim()) {
        return;
      }

      counter += 1;
      const label = counter === 1 ? 'Query Results' : `Query Results #${counter}`;
      const parsedResult = buildSqlResult(csvText, message.sql_query ?? '', label);

      if (!parsedResult) {
        return;
      }

      let relatedMessageId: string | undefined;
      let sourcePrompt: string | undefined;

      for (let i = messageIndex - 1; i >= 0; i -= 1) {
        const candidate = messages[i];
        if (candidate.type !== 'human') {
          continue;
        }

        if (typeof candidate.content === 'string') {
          const trimmedPrompt = candidate.content.trim();
          sourcePrompt = trimmedPrompt.length > 0 ? trimmedPrompt : undefined;
        }

        break;
      }

      for (let i = messageIndex + 1; i < messages.length; i += 1) {
        const candidate = messages[i];
        if (candidate.type === 'human') {
          break;
        }

        if (candidate.type === 'ai') {
          relatedMessageId = candidate.id;
          break;
        }
      }

      results.push({
        ...parsedResult,
        id: message.id,
        timestampLabel: formatResultTimestampLabel(message.metadata),
        relatedMessageId,
        sourcePrompt,
      });
    });

    return results;
  }, [messages]);

  const sqlResultIdsByAiMessageId = useMemo(() => {
    const mapping = new Map<string, string[]>();

    threadSqlResults.forEach((result) => {
      if (!result.relatedMessageId) {
        return;
      }

      if (!mapping.has(result.relatedMessageId)) {
        mapping.set(result.relatedMessageId, []);
      }

      const existingResultIds = mapping.get(result.relatedMessageId);
      if (!existingResultIds) {
        return;
      }

      existingResultIds.push(result.id);
    });

    return mapping;
  }, [threadSqlResults]);

  const sqlResultById = useMemo(() => {
    const mapping = new Map<string, SqlResultEntry>();

    threadSqlResults.forEach((result) => {
      mapping.set(result.id, result);
    });

    return mapping;
  }, [threadSqlResults]);

  useEffect(() => {
    hasInitializedResultSignalRef.current = false;
    lastSeenSqlResultIdRef.current = null;
    setHasUnseenDataResults(false);
  }, [threadId]);

  useEffect(() => {
    const latestResultId = threadSqlResults[threadSqlResults.length - 1]?.id ?? null;

    if (!latestResultId) {
      if (isDataDictionaryOpen) {
        setHasUnseenDataResults(false);
      }

      return;
    }

    if (!hasInitializedResultSignalRef.current) {
      hasInitializedResultSignalRef.current = true;
      lastSeenSqlResultIdRef.current = latestResultId;
      setHasUnseenDataResults(false);
      return;
    }

    if (isDataDictionaryOpen) {
      lastSeenSqlResultIdRef.current = latestResultId;
      setHasUnseenDataResults(false);
      return;
    }

    if (isThreadLoading || isThreadHistoryLoading) {
      return;
    }

    if (lastSeenSqlResultIdRef.current !== latestResultId) {
      setHasUnseenDataResults(true);
    }
  }, [isDataDictionaryOpen, isThreadHistoryLoading, isThreadLoading, threadId, threadSqlResults]);

  return {
    threadSqlResults,
    sqlResultIdsByAiMessageId,
    sqlResultById,
    hasUnseenDataResults,
  };
};
